// The Creative Library's detail drawer: approval, provider publishing, assets,
// name and agency, the content fields, and the version archive. Split out of
// `library.tsx`, which keeps the grid and re-exports this drawer for the
// campaign creative workspace.

import { Fragment, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import type { Creative, Production } from '@/api/types';
import type { CreativePatchInput } from '@/api/manage-types';
import type { DropdownOption } from '@/components/dropdowns';
import { CreativeContentEditor } from '@/components/creative-content-editor';
import { DisplayAnimationNote } from '@/components/display-animation-note';
import { creativePatch } from '@/lib/creative-patch';
import { useCan } from '@/auth/capabilities';
import { ResponsiveOverlay } from '@/components/responsive-overlay';
import { Icons, ChannelPill } from '@/components/primitives';
import { StatusPill } from '@/components/status-pill';
import { CopyableId } from '@/components/copyable-id';
import { FailureNotice } from '@/components/failure-notice';
import { CreativeVersionsPanel } from '@/components/creative-versions';
import { revisionSavePlan } from '@/lib/creative-revision-policy';
import {
  useApproveCreative,
  useArchiveCreative,
  useCreativePreviewStatus,
  useCreativeRevisions,
  useDeleteCreative,
  usePatchCreative,
  useRestoreCreative,
  useRetryCreativeSync,
  useSubmitCreativeForReview,
} from '@/api/manage-hooks';
import { reportActionError } from '@/lib/report-action-error';
import { RequiredField, useRequiredFeedback } from '@/components/required-field';
import { MetaPreviewGallery } from '@/components/meta-preview-gallery';
import {
  ExternalApprovalRecord,
  ShareAgencyModal,
} from '@/components/creative-review-share';
import { fmtDateTime, withEt } from '@/lib/datetime';
import {
  CHANNEL_FIELDS,
  creativeSpecDisplay,
  isValidCreativeDestinationUrl,
  isValidMetaDisplayUrl,
  metaCopyIssues,
  metaFieldsFromCreative,
  serializeMetaCreativeSpec,
  withoutStoredCtaRefusal,
  type CreativeFieldValues,
} from './creative-config';
import { CreativeFieldRow } from './creative-field-row';
import { creativeSyncLabel, creativeSyncView } from '@/lib/creative-sync-view';
import { creativeArchiveLock, creativeEditLock } from '@/lib/creative-lock';
import { LockBanner } from '@/components/lock-primitives';
import { deliveryTargetFor } from '@/lib/campaign-publish-sequence';
import { placementLabel } from '@/lib/creative-placement-labels';

/** The library's channel filter: every channel, or one. */
export type LibraryChannelFilter = 'all' | Creative['channel'];

export const LIB_CHANNEL_OPTS: readonly DropdownOption<LibraryChannelFilter>[] = [
  { id: 'all',  label: 'All channels' },
  { id: 'meta', label: 'Meta' },
  { id: 'ctv',  label: 'CTV' },
  { id: 'dsp',  label: 'Display' },
];

export interface VersionTagProps {
  c: Pick<Creative, 'revision' | 'approved_revision'>;
}

/// `v3` on the current revision, `v3 · v2 approved` while a newer draft sits
/// over the version campaigns actually get.
export function VersionTag({ c }: VersionTagProps) {
  if (!c.revision) return null;
  const newerDraft = c.approved_revision != null && c.approved_revision !== c.revision;
  return (
    <span className="tag tag-version" data-testid="version-tag">
      v{c.revision}{newerDraft ? ` · v${c.approved_revision} approved` : ''}
    </span>
  );
}

/// Field values for the drawer, keyed by CHANNEL_FIELDS keys.
///
/// `ad_name` and `dest_url` are top-level creative columns; the remaining Meta
/// keys live inside the `spec` JSON. `agency` is not a CHANNEL_FIELDS entry but
/// is editable, so it rides alongside.
function editStateFor(creative: Creative | null | undefined): CreativeFieldValues {
  return {
    ad_name: creative?.name || '',
    dest_url: creative?.destination_url || '',
    agency: creative?.agency || '',
    ...(creative?.channel === 'meta' ? metaFieldsFromCreative(creative) : {}),
  };
}

/// The PATCH body for the current edit state. `format` is a generated display
/// string and `spec` for CTV/Display is a dimension string — neither is user
/// copy, so both are passed through untouched rather than exposed as inputs.
function patchBodyFor(creative: Creative, edit: CreativeFieldValues): CreativePatchInput {
  // Only send `spec` when the copy actually changed. Re-serializing it on every
  // save meant a rename rewrote the spec, and a spec edit is what
  // `edit_requires_republish` keys off — so renaming an approved creative
  // silently knocked it back to review. It also resubmitted legacy specs that
  // were never JSON, which the API now rejects.
  const nextSpec = creative.channel === 'meta'
    ? serializeMetaCreativeSpec(edit)
    : creative.spec;
  const body: CreativePatchInput = {
    name: (edit.ad_name || '').trim(),
    format: creative.format,
    destination_url: (edit.dest_url || '').trim(),
    agency: (edit.agency || '').trim() || null,
  };
  if (nextSpec !== creative.spec) body.spec = nextSpec;
  return creativePatch(creative, body);
}

/// The copy fields whose save refusal renders beside the field itself. The
/// save gate already reported them; without an anchor a refused CTA ("Book
/// Now", which the dropdown no longer lists) made Save do nothing visible.
const DRAWER_COPY_KEYS: readonly string[] = ['post_text', 'headline', 'cta'];

function isDirty(creative: Creative, edit: CreativeFieldValues): boolean {
  const next = patchBodyFor(creative, edit);
  // `spec` is now absent from the body when unchanged, so its presence IS the
  // signal that the copy moved.
  return next.name !== creative.name
    || next.spec !== undefined
    || next.destination_url !== undefined
    || (next.agency || '') !== (creative.agency || '');
}

/** The drawer's two tabs; `?tab=` on `/creatives` carries the same pair. */
export type CreativeDrawerTab = 'details' | 'versions';

/** The production a drawer names: only its display name is read. */
export type CreativeDrawerProduction = Pick<Production, 'name'>;

/** One label and its value in the drawer's "Asset metadata" list. */
type MetadataRow = [label: string, value: ReactNode];

/** `true` for an in-place save, the minted revision when the save made one. */
type SaveReceipt = boolean | { revision: number };

export interface CreativeDetailDrawerProps {
  creative: Creative;
  production: CreativeDrawerProduction | null | undefined;
  onClose: () => void;
  onUpdated: ((creative: Creative) => void) | undefined;
  onDeleted: ((creativeId: string) => void) | undefined;
  initialTab?: CreativeDrawerTab;
  onTabChange?: ((tab: CreativeDrawerTab) => void) | undefined;
}

export function CreativeDetailDrawer({ creative, production, onClose, onUpdated, onDeleted, initialTab = 'details', onTabChange }: CreativeDetailDrawerProps) {
  const canEdit = useCan('creatives_edit');
  const [contentEditor, setContentEditor] = useState(false);
  const patchCreative = usePatchCreative();
  const [tab, setTab] = useState<CreativeDrawerTab>(initialTab);
  useEffect(() => { setTab(initialTab); }, [initialTab, creative?.id]);
  const pickTab = (next: CreativeDrawerTab) => { setTab(next); onTabChange?.(next); };
  const deleteCreative = useDeleteCreative();
  const submitForReview = useSubmitCreativeForReview();
  const approveCreative = useApproveCreative();
  const [current, setCurrent] = useState<Creative>(creative);
  // Edit state is keyed by CHANNEL_FIELDS keys, not by wire columns, so the
  // drawer offers the same fields the creative was built with. `spec` is never
  // edited directly — for Meta it is composed from the copy fields on save, and
  // for CTV/Display it is a dimension string that is not user copy at all.
  const [edit, setEdit] = useState<CreativeFieldValues>(() => editStateFor(creative));
  // `false`, `true`, or `{ revision }` when the save minted a new version.
  const [saved, setSaved] = useState<SaveReceipt>(false);
  const [actionError, setActionError] = useState('');
  const [shareOpen, setShareOpen] = useState(false);
  const required = useRequiredFeedback();
  const agencyInputId = useId();
  useEffect(() => {
    setCurrent(creative);
    setEdit(editStateFor(creative));
    setSaved(false);
    setActionError('');
    setShareOpen(false);
    required.clearAll();
  }, [creative?.id]);
  // Opening a different creative resets everything above; a fresh copy of the
  // SAME creative only replaces what nobody is editing. Following the prop
  // matters after the content editor reports a 409 and invalidates
  // `creatives.byId` / `creatives.list`: the advice is "close and reopen the
  // editor", and without this the refetched version never reaches the drawer,
  // so reopening hands back the same stale snapshot and the same 409.
  const editRef = useRef(edit);
  editRef.current = edit;
  const currentRef = useRef(current);
  currentRef.current = current;
  useEffect(() => {
    if (!creative) return;
    const shown = currentRef.current || creative;
    if (shown.id === creative.id && isDirty(shown, editRef.current)) return;
    setCurrent(creative);
    setEdit(editStateFor(creative));
  }, [creative]);
  if (!creative) return null;
  const c = current || creative;
  const channelLabel = c.channel === 'meta' ? 'Meta' : c.channel === 'ctv' ? 'CTV' : 'Display';
  const hasUploadedAsset = c.assets.some(asset => asset.upload_status === 'uploaded');
  // Meta shares its official previews, Display its banners; CTV has no review
  // page and the mint endpoint refuses it (409).
  const canShare = (c.channel === 'meta' || c.channel === 'dsp') && (c.status === 'review' || c.status === 'approved');
  const actionPending =
    !canEdit || patchCreative.isPending
    || deleteCreative.isPending
    || submitForReview.isPending
    || approveCreative.isPending;
  const dirty = isDirty(c, edit);
  // Only an archived creative is locked in place; everything else has a path.
  // What that path IS — save in place, or mint the next version because this
  // one is approved or a published campaign flies it — is decided in
  // `@/lib/creative-revision-policy`, where a test can reach it, and said
  // before the click: the button names the version, the strip above it names
  // who keeps what.
  const lock = creativeEditLock(c);
  const archiveLock = creativeArchiveLock(c);
  const savePlan = revisionSavePlan(c, patchBodyFor(c, edit));
  const revisionsQuery = useCreativeRevisions(c.id, { enabled: tab === 'versions' });
  const applyResult = (updated: Creative) => {
    setCurrent(updated);
    setEdit(editStateFor(updated));
    onUpdated?.(updated);
  };
  const runAction = async (action: () => Promise<Creative>) => {
    setActionError('');
    try {
      applyResult(await action());
    } catch (error) {
      setActionError(reportActionError(error, {
        operation: 'creative.drawer_action',
        fallback: 'The creative could not be updated.',
      }));
    }
  };
  const retryCreativeSync = useRetryCreativeSync();
  // `c.sync` comes from the `cached` creatives list and is as old as its
  // staleTime, so it cannot narrate a publish that is happening right now.
  // This is the `realtime` companion the sync gate already uses; it polls only
  // while the work is unfinished and switches itself off when `terminal`.
  const previewStatus = useCreativePreviewStatus(c.id, {
    enabled: c.status === 'approved',
  }).data;
  // Derived in `@/lib/creative-sync-view` rather than here, so the decision
  // has a unit test of its own instead of only this drawer's DOM test.
  const syncView = creativeSyncView(c.channel, c.sync, previewStatus);
  const { settled: syncSettled, failureCode: syncFailureCode, failure: syncFailure, providerCreativeId, retriesExhausted } = syncView;
  const syncPhase = syncView.phase;
  const [retriedThisSession, setRetriedThisSession] = useState(false);
  const archiveCreative = useArchiveCreative();
  const restoreCreative = useRestoreCreative();
  const approveAndTrackPublication = async () => {
    await runAction(async () => {
      // Provider publishing runs entirely server-side; the Destinations panel
      // below and the card chip show its progress.
      return approveCreative.mutateAsync(c.id);
    });
  };
  return (
    <>

      {contentEditor && <CreativeContentEditor creative={c} onClose={()=>setContentEditor(false)} onSaved={applyResult} />}
      <ShareAgencyModal
        open={shareOpen}
        onClose={()=>setShareOpen(false)}
        creativeIds={[c.id]}
        creativeNames={{ [c.id]: c.name }}
        production={production ? { title: production.name } : null}
        channel={c.channel === 'dsp' ? 'dsp' : 'meta'}
      />
      <ResponsiveOverlay label="library details" onClose={onClose} className="drawer">
        <div className="drawer-hd">
          <div className="creative-drawer-title">
            <div className="muted mono creative-drawer-eyebrow" style={{fontSize:10,letterSpacing:'.1em'}}>{production?.name || '—'} · {c.format}</div>
            <h2 style={{fontFamily:'var(--font-display)',fontSize:22,fontWeight:800,letterSpacing:'.01em',textTransform:'uppercase',margin:'4px 0 6px'}}>{c.name}</h2>
            <div className="flex items-center gap-2 creative-drawer-chips">
              <StatusPill status={c.status}/>
              <VersionTag c={c}/>
              <ChannelPill channel={c.channel}/>
              <span className="tag">{c.media_type === 'video' ? '▶ VIDEO' : '■ IMAGE'}</span>
              <span className="tag">{creativeSpecDisplay(c)}</span>
            </div>
          </div>
          <button className="btn btn-sm btn-ghost" onClick={onClose} style={{fontSize:18,padding:'4px 10px'}}>×</button>
        </div>

        <div className="drawer-tabs" role="tablist" aria-label="Creative">
          <button type="button" role="tab" id="creative-tab-details" className="drawer-tab" aria-selected={tab === 'details'} aria-controls="creative-panel-details" onClick={() => pickTab('details')}>Details</button>
          <button type="button" role="tab" id="creative-tab-versions" className="drawer-tab" aria-selected={tab === 'versions'} aria-controls="creative-panel-versions" onClick={() => pickTab('versions')}>
            Versions <span className="drawer-tab-count">{c.revision || 1}</span>
          </button>
        </div>

        {tab === 'versions' && (
          <div className="drawer-body" id="creative-panel-versions" role="tabpanel" aria-labelledby="creative-tab-versions">
            <CreativeVersionsPanel
              channel={c.channel}
              creativeStatus={c.status}
              currentRevision={c.revision}
              approvedRevision={c.approved_revision ?? null}
              revisions={revisionsQuery.data}
              isPending={revisionsQuery.isPending}
              error={revisionsQuery.error}
              onRetry={() => { void revisionsQuery.refetch(); }}
            />
          </div>
        )}

        <div className="drawer-body" id="creative-panel-details" role="tabpanel" aria-labelledby="creative-tab-details" hidden={tab !== 'details'}>
          <div className="field-lbl mb-2">Approval status</div>
          <div className="panel" style={{padding:12,marginBottom:14}}>
            <div className="flex between items-center mb-2" style={{gap:10}}>
              <div className="flex items-center gap-2">
                <StatusPill status={c.status}/>
                <span className="muted mono" style={{fontSize:10.5,letterSpacing:'.06em'}}>
                  {c.status === 'approved' ? 'Cleared for use in campaigns' :
                   c.status === 'review'   ? 'Awaiting agency / internal sign-off' :
                   c.status === 'draft'    ? 'Not yet submitted for review' : 'Archived asset'}
                </span>
              </div>
            </div>
            {c.approved_revision != null && c.approved_revision !== c.revision && (
              <div className="field-hint" style={{marginBottom:8}}>
                Version {c.approved_revision} is approved and available to campaigns; version {c.revision} is the draft you are editing.
              </div>
            )}
            <div className="flex gap-2" style={{flexWrap:'wrap'}}>
              {c.status === 'draft' && (
                <button
                  className="btn btn-sm"
                  onClick={()=>void runAction(() => submitForReview.mutateAsync(c.id))}
                  disabled={actionPending}
                >Submit for review</button>
              )}
              {(c.status === 'review' || (c.status === 'draft' && c.channel !== 'meta')) && (
                <button
                  className="btn btn-sm btn-primary"
                  onClick={()=>{
                    if (!required.report([
                      !hasUploadedAsset && {
                        key: 'creative-drawer-assets',
                        message: 'Upload and attach at least one asset before approval.',
                      },
                      !isValidCreativeDestinationUrl(c.destination_url) && {
                        key: 'creative-drawer-destination',
                        message: 'Enter and save a valid HTTPS destination URL before approval.',
                      },
                    ])) return;
                    void approveAndTrackPublication();
                  }}
                  disabled={actionPending}
                  title={!hasUploadedAsset ? 'Upload and attach an asset before approval' : ''}
                >Approve locally</button>
              )}
              {c.status === 'approved' && (
                <button className="btn btn-sm btn-primary" disabled>✓ Approved</button>
              )}
              {/* Sharing needs something to review: Meta's rendered previews,
                  which exist from submit-for-review onward, or Display's
                  banners. A draft is not offered, and CTV cannot be shared. */}
              {canShare && (
                <button className="btn btn-sm" onClick={()=>setShareOpen(true)}>
                  {Icons.send} Share for approval
                </button>
              )}
            </div>
            {/* What approval does on this creative's own channel: only Meta stages
              assets and previews at submission; Display and CTV go to The Trade Desk
              when approved (`creatives::approve`, creative_sync::ttd_upsert). */}
            <div className="field-hint mt-2">
              {c.channel === 'meta'
                ? 'Meta submission stages inert provider assets and generates official previews. Delivery objects are not created here.'
                : c.channel === 'dsp'
                  ? 'Approving sends each banner to The Trade Desk as an image creative for its review. Delivery objects are not created here.'
                  : 'Approving uploads the video to The Trade Desk as a creative for its review. Delivery objects are not created here.'}
            </div>
            {/* The save/action error now renders in the footer beside the button
              that triggers it — see .drawer-foot below. */}
          </div>

          {c.status === 'approved' && (
            <>
              <div className="field-lbl mb-2">Destination publishing</div>
              <div className="panel" style={{padding:'10px 14px',marginBottom:14}}>
                {/* Publishing runs entirely server-side (self-scheduled queue
                    polling + the minute cron); this panel only reports it. */}
                <div className="stack-phone" style={{display:'grid',gridTemplateColumns:'56px 130px 1fr auto',gap:10,alignItems:'baseline',fontSize:12}}>
                  <span className="mono" style={{fontSize:10.5,letterSpacing:'.1em'}}>
                    {(c.sync?.target ?? deliveryTargetFor(c.channel)).toUpperCase()}
                  </span>
                  <span className="mono" style={{fontSize:11}}>
                    {creativeSyncLabel(syncView)}
                  </span>
                  <div style={{minWidth:0}}>
                    {syncPhase === 'failed' ? (
                      <FailureNotice
                        dense
                        failure={syncFailure}
                        fallback={syncFailureCode || c.sync?.last_error || 'Provider publishing failed.'}
                      />
                    ) : (
                      <div className="muted" style={{fontSize:11,overflowWrap:'anywhere'}}>
                        {providerCreativeId
                          ? <>AdCreative <span className="mono">{providerCreativeId}</span></>
                          : 'Encoding and review at the provider can take a while; no action needed.'}
                      </div>
                    )}
                  </div>
                  {syncPhase === 'failed' && (
                    <button
                      className="btn btn-sm"
                      disabled={retryCreativeSync.isPending}
                      onClick={() => {
                        setRetriedThisSession(true);
                        void retryCreativeSync.mutateAsync(c.id);
                      }}
                    >
                      {retryCreativeSync.isPending ? 'Retrying…' : 'Retry'}
                    </button>
                  )}
                </div>
                {/* The retry POST only enqueues; the publish itself takes
                    seconds and used to finish with nothing on screen changing,
                    because the mutation invalidated a resource this drawer does
                    not read. `useCreativePreviewStatus` is `realtime` and stops
                    polling once the sync is terminal, so this line tracks the
                    work and then stays put as the receipt. */}
                {retriedThisSession && syncSettled && (
                  <div
                    className="muted"
                    style={{marginTop:10,paddingTop:10,borderTop:'1px solid var(--line-2)',fontSize:11.5}}
                  >
                    {syncPhase === 'synced'
                      ? <>Published to {(c.sync?.target ?? c.channel).toUpperCase()} · <span className="mono">{providerCreativeId ?? 'id pending'}</span></>
                      : <>Failed · {syncFailure?.headline ?? <span className="mono">{syncFailureCode ?? 'unknown'}</span>}</>}
                  </div>
                )}
                {/* `attempts` is on the payload and was never surfaced. The
                    background retry cron abandons a creative at
                    PROVIDER_RETRY_ATTEMPT_CAP, after which the row looks exactly
                    like one that failed once — which is what turned a deploy
                    into a manual hunt on 2026-08-17. */}
                {syncPhase === 'failed' && retriesExhausted && (
                  <div style={{marginTop:8,fontSize:11.5}}>
                    Automatic retries are exhausted ({previewStatus?.attempts} attempts).
                    This will not publish again on its own.
                  </div>
                )}
              </div>
            </>
          )}

          {c.channel === 'meta' && (c.status === 'review' || c.status === 'approved') && (
            <div style={{marginBottom:14}}>
              {/* Browsing must not enqueue a provider sync: no autoRefresh, so
                  nothing here asks for a fresh preview bundle in the
                  background. Decision buttons are a different question — they
                  write an audit row, so they appear only for a user who may
                  edit, and only while the creative is still in review. */}
              <MetaPreviewGallery creativeId={c.id} readOnly={!canEdit || c.status !== 'review'} />
              {/* Advisory: external sign-off is recorded and shown, but a
                  signed-in user still makes the Review → Approved transition. */}
              <ExternalApprovalRecord creativeId={c.id} />
            </div>
          )}
          {c.channel === 'dsp' && (c.status === 'review' || c.status === 'approved') && (
            <ExternalApprovalRecord creativeId={c.id} />
          )}

          <RequiredField
            fieldKey="creative-drawer-assets"
            message={required.messages['creative-drawer-assets']}
            attempt={required.attempt}
            surface
          >
            <div className="field-lbl mb-2">Attached assets · {c.assets.length}</div>
            {canEdit && !lock && <button className="btn" onClick={()=>setContentEditor(true)}>Edit assets and content</button>}
            {c.assets.length === 0 ? (
              <div className="panel muted" style={{padding:14,marginBottom:14,fontSize:11.5}}>
                No uploaded asset is attached to this creative.
              </div>
            ) : (
              <div className="grid-3" style={{gridTemplateColumns:'repeat(auto-fill,minmax(170px,1fr))',gap:10,marginBottom:14}}>
                {c.assets.map(asset => (
                  <div key={asset.asset_id} className="panel" style={{padding:10}}>
                    {/* These tiles listed metadata with no image at all, even
                        though view_url is on every asset in the list payload.
                        Scale to fit and centre so a 9:16 story and a 1:1 square
                        sit in the same tile without either being cropped. */}
                    {asset.view_url && asset.upload_status === 'uploaded' && (
                      <div className="asset-thumb">
                        {asset.kind === 'video'
                          ? <video src={asset.view_url} muted loop playsInline className="poster-media"/>
                          : <img src={asset.view_url} alt="" loading="lazy" className="poster-media"/>}
                      </div>
                    )}
                    <div className="flex between items-center" style={{gap:8}}>
                      <span className="tag">{asset.kind.toUpperCase()}</span>
                      <span className="tag" style={{color:asset.upload_status==='uploaded'?'var(--good)':'var(--warn)'}}>{asset.upload_status}</span>
                    </div>
                    {/* Named by what it serves. SAM keeps no file name, and the asset id and the
                        wire tags ("META_SQ") meant nothing to the person reading the card. */}
                    <div className="creative-asset-title" style={{fontSize:12,fontWeight:600,marginTop:8}}>
                      {asset.placement_tags.map(placementLabel).join(' · ') || (asset.kind === 'video' ? 'Video' : 'Image')}
                    </div>
                    <div className="muted mono" style={{fontSize:9.5,marginTop:5}}>
                      {asset.width && asset.height ? `${asset.width}×${asset.height}` : 'Dimensions unavailable'}
                      {asset.duration_ms ? ` · ${(asset.duration_ms / 1000).toFixed(1)}s` : ''}
                    </div>
                    <div className="muted mono" style={{fontSize:9.5,marginTop:3}}>
                      {(Number(asset.byte_size) / 1024).toFixed(1)} KB
                    </div>
                    {c.channel === 'dsp' && <DisplayAnimationNote animation={asset.animation} byteSize={asset.byte_size}/>}
                  </div>
                ))}
              </div>
            )}
          </RequiredField>

          <div className="field-lbl mb-2">Name &amp; agency · saved in place</div>
          <div className="panel" style={{padding:'12px 14px',marginBottom:14}} data-locked={lock ? lock.kind : undefined}>
            {lock && (
              <LockBanner inset title="Not editable">
                {lock.message}
              </LockBanner>
            )}
            {(CHANNEL_FIELDS[c.channel] || []).filter(field => field.key === 'ad_name').map(field => (
              <RequiredField
                key={field.key}
                fieldKey="creative-drawer-name"
                message={required.messages['creative-drawer-name']}
                attempt={required.attempt}
              >
                <CreativeFieldRow
                  field={field}
                  value={edit[field.key]}
                  disabled={!!lock}
                  onChange={(key, next) => {
                    setEdit(previous => ({ ...previous, [key]: next }));
                    setSaved(false);
                    if (next.trim()) required.clear('creative-drawer-name');
                  }}
                />
              </RequiredField>
            ))}
            <div className="field" style={{marginBottom:0}}>
              <label className="field-lbl" htmlFor={agencyInputId}>Agency</label>
              <input
                id={agencyInputId}
                className={`input${lock ? ' input-locked' : ''}`}
                disabled={!!lock}
                value={edit.agency || ''}
                onChange={event => {
                  const next = event.target.value;
                  setEdit(previous => ({ ...previous, agency: next }));
                  setSaved(false);
                }}
              />
            </div>
          </div>

          <div className="field-lbl mb-2" style={{display:'flex',alignItems:'center',gap:8}}>
            <span>Content · version {c.revision} · {channelLabel}</span>
          </div>
          <div className="panel" style={{padding:'12px 14px',marginBottom:14}} data-content-revision={c.revision}>
            <div className="field-hint" style={{marginBottom:10}}>
              {c.status === 'approved' || c.pinned_by?.some(pin => pin.creative_revision === c.revision)
                ? 'Changes here save as a new version; campaigns keep the version they approved.'
                : 'Changes here save into this version.'}
            </div>
            {(CHANNEL_FIELDS[c.channel] || []).filter(field => field.key !== 'ad_name').map(field => {
              const row = (
                <CreativeFieldRow
                  field={field}
                  value={edit[field.key]}
                  disabled={!!lock}
                  onChange={(key, next) => {
                    setEdit(previous => ({ ...previous, [key]: next }));
                    setSaved(false);
                    if (key === 'ad_name' && next.trim()) required.clear('creative-drawer-name');
                    if (key === 'dest_url' && isValidCreativeDestinationUrl(next)) {
                      required.clear('creative-drawer-destination');
                    }
                    if (key === 'display_url' && isValidMetaDisplayUrl(next)) {
                      required.clear('creative-drawer-display-url');
                    }
                    if (DRAWER_COPY_KEYS.includes(key)
                      && !metaCopyIssues({ [key]: next }).some((issue) => issue.key === key)) {
                      required.clear(`creative-drawer-${key}`);
                    }
                  }}
                />
              );
              const requiredKey = field.key === 'ad_name'
                ? 'creative-drawer-name'
                : field.key === 'dest_url'
                  ? 'creative-drawer-destination'
                  : field.key === 'display_url'
                    ? 'creative-drawer-display-url'
                    : c.channel === 'meta' && DRAWER_COPY_KEYS.includes(field.key)
                      ? `creative-drawer-${field.key}`
                      : null;
              return requiredKey ? (
                <RequiredField
                  key={field.key}
                  fieldKey={requiredKey}
                  message={required.messages[requiredKey]}
                  attempt={required.attempt}
                >
                  {row}
                </RequiredField>
              ) : <Fragment key={field.key}>{row}</Fragment>;
            })}
          </div>

          {/* The four "Lifetime performance" tiles were hardcoded '—' strings,
              never wired to any source. Four permanent em-dashes read as broken
              rather than as "not yet available", so they are gone until a
              reporting roll-up exists to fill them. */}

          <div className="field-lbl mb-2">Asset metadata</div>
          <div className="panel" style={{padding:'4px 12px',marginBottom:14}}>
            {([
              // Neither id was reachable from this screen, so quoting a
              // creative in a bug report or a log-triage query meant reading it
              // out of a URL. The provider id is the one that ties a SAM
              // creative to an object in Ads Manager.
              ['Creative ID', <CopyableId key="cid" value={c.id}/>],
              ...(providerCreativeId
                ? [[c.channel === 'meta' ? 'Meta AdCreative' : 'Provider creative',
                    <CopyableId key="pid" value={providerCreativeId}/>] satisfies MetadataRow]
                : []),
              ['Production', production?.name || '—'],
              ['Channel',    LIB_CHANNEL_OPTS.find(o=>o.id===c.channel)?.label || c.channel],
              ['Format',     c.format],
              ['Media',      c.media_type === 'video' ? 'Video' : 'Image'],
              ['Assets',     `${c.assets.length} attached · ${c.assets.reduce((total, asset) => total + asset.placement_tags.length, 0)} placement${c.assets.reduce((total, asset) => total + asset.placement_tags.length, 0) === 1 ? '' : 's'}`],
              ['Spec',       creativeSpecDisplay(c)],
              ['Destination', c.destination_url],
              ['Uploaded',   c.created_at ? withEt(fmtDateTime(c.created_at)) : '—'],
              ['Last used',  c.last_used_at ? withEt(fmtDateTime(c.last_used_at)) : '—'],
              ['Creator',    c.created_by || '—'],
              ['Agency',     c.agency || '—'],
            ] satisfies MetadataRow[]).map(([k,v]) => (
              <div key={k} style={{display:'grid',gridTemplateColumns:'120px 1fr',padding:'8px 0',borderBottom:'1px solid var(--line-2)',fontSize:12}}>
                <div className="muted mono" style={{fontSize:10.5,letterSpacing:'.06em',textTransform:'uppercase'}}>{k}</div>
                <div>{v}</div>
              </div>
            ))}
          </div>
        </div>

        {tab === 'details' && !lock && dirty && savePlan.notice && !actionError && (
          <div className="drawer-notice" role="status">
            <span className="drawer-notice-ico" aria-hidden="true">{Icons.layers}</span>
            <span>{savePlan.notice}</span>
          </div>
        )}
        <div className="drawer-foot" hidden={tab !== 'details'}>
          {(c.status === 'review' || c.status === 'approved') && (
            <button
              className="btn btn-sm"
              disabled={actionPending || !!archiveLock}
              title="Hide this creative from the library and every picker; provider objects are kept and restore re-adopts them"
              onClick={() => {
                if (!window.confirm(`Archive creative "${c.name}"? It disappears from pickers; restore brings it back as a draft.`)) return;
                void runAction(() => archiveCreative.mutateAsync(c.id));
              }}
            >Archive</button>
          )}
          {(c.status === 'review' || c.status === 'approved') && archiveLock && (
            <span className="muted creative-lock-reason" style={{fontSize:11}}>
              {archiveLock.message}
            </span>
          )}
          {c.status === 'archived' && (
            <button
              className="btn btn-sm"
              disabled={actionPending}
              title="Restore as a draft — approval is re-earned through the normal actions"
              onClick={() => {
                void runAction(() => restoreCreative.mutateAsync(c.id));
              }}
            >Restore</button>
          )}
          {c.status === 'draft' && (
            <button
              className="btn btn-sm"
              disabled={actionPending}
              onClick={async () => {
                if (!window.confirm(`Delete draft creative "${c.name}"?`)) return;
                setActionError('');
                try {
                  await deleteCreative.mutateAsync(c.id);
                  onDeleted?.(c.id);
                  onClose();
                } catch (error) {
                  setActionError(reportActionError(error, {
                    operation: 'creative.delete_draft',
                    fallback: 'The draft could not be deleted.',
                    context: { entity_kind: 'creative', entity_id: c.id },
                  }));
                }
              }}
            >Delete draft</button>
          )}
          {/* Errors used to render in a panel at the top of the scrolling body,
              ~250px above this button — so a failed save looked like nothing
              happened. Keep the outcome next to the control that caused it. */}
          <div className="drawer-foot-primary" style={{marginLeft:'auto',display:'flex',alignItems:'center',gap:10}}>
            {actionError && (
              <span className="asset-err" style={{margin:0,padding:'6px 9px'}}>
                <span>⚠</span><span>{actionError}</span>
              </span>
            )}
            {!actionError && saved && (
              <span className="muted" style={{fontSize:11.5,color:'var(--good)'}}>
                {Icons.check} {typeof saved === 'object'
                  ? <>Saved as version {saved.revision} · Draft · <a href="#versions" onClick={event => { event.preventDefault(); pickTab('versions'); }}>View versions</a></>
                  : 'Saved'}
              </span>
            )}
            {/* The reason sits beside the control it explains, as visible
                text — a `title` never shows on a phone. */}
            {!actionError && lock && (
              <span className="muted creative-lock-reason" style={{fontSize:11}}>
                {lock.message}
              </span>
            )}
            <button
              className={`btn btn-primary${patchCreative.isPending ? ' btn-busy' : ''}`}
              disabled={actionPending || !dirty || !!lock}
              title={lock ? undefined : dirty ? undefined : 'No changes to save'}
              onClick={() => {
                const valid = required.report([
                  !(edit.ad_name || '').trim() && {
                    key: 'creative-drawer-name',
                    message: 'Enter a creative name before saving.',
                  },
                  !isValidCreativeDestinationUrl((edit.dest_url || '').trim()) && {
                    key: 'creative-drawer-destination',
                    message: 'Enter a valid HTTPS destination URL before saving.',
                  },
                  c.channel === 'meta'
                    && !isValidMetaDisplayUrl((edit.display_url || '').trim()) && {
                    key: 'creative-drawer-display-url',
                    message: 'Enter a valid display link, e.g. telecharge.com.',
                  },
                  // The API requires these. Legacy Meta creatives whose stored
                  // spec was never JSON (e.g. "Multi-placement") hydrate the
                  // drawer with blanks, so without this the drawer looked
                  // saveable and the server answered 422. A stored CTA Meta
                  // refuses ("Book Now") is refused only when this PATCH
                  // carries `spec`: a rename, destination or agency edit
                  // leaves the copy out and the API accepts it.
                  ...(c.channel === 'meta'
                    ? (patchBodyFor(c, edit).spec === undefined
                        ? withoutStoredCtaRefusal(metaCopyIssues(edit), edit)
                        : metaCopyIssues(edit))
                        .filter((issue) => issue.key !== 'display_url')
                        .map((issue) => ({
                          key: `creative-drawer-${issue.key}`,
                          message: issue.message,
                        }))
                    : []),
                ]);
                if (!valid) return;
                void runAction(async () => {
                  const { creative: updated, created_revision } = await patchCreative.mutateAsync({
                    id: c.id,
                    patch: { ...patchBodyFor(c, edit), expected_revision: c.revision },
                  });
                  setEdit(editStateFor(updated));
                  setSaved(created_revision ? { revision: created_revision } : true);
                  return updated;
                });
              }}
            >
              {patchCreative.isPending
                ? <><span className="btn-spinner" aria-hidden="true" />Saving…</>
                : <>{Icons.check} {savePlan.buttonLabel}</>}
            </button>
          </div>
        </div>
      </ResponsiveOverlay>

    </>
  );
}
